import React from "react";
import Layout from "@/components/Layout";
import PageMeta from "@/components/seo/PageMeta";
import { Button } from "@/components/ui/button";
import { RefreshCw, FileText, Bell, Users, CheckCircle, Calendar } from "lucide-react";
import { BOOKING_URL } from "@/config/constants";

const SubscriptionPage: React.FC = () => {
  return (
    <Layout>
      <PageMeta
        title="Governance Subscription | BoardSight AI"
        description="Stay current on AI governance with ongoing updates, resources, and expert guidance for board directors."
      />
      
      <div className="bg-gradient-to-b from-techne-primaryPurple/10 to-transparent">
        <div className="container-custom pt-16 pb-20">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold text-techne-primaryPurple mb-6">
              Governance Subscription
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Stay ahead of the rapidly evolving AI landscape with continuous updates, resources, and expert guidance.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto mb-16">
            <div className="bg-card rounded-xl p-6 shadow-lg border text-center">
              <RefreshCw className="h-10 w-10 text-techne-purple mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">Continuous Updates</h3>
              <p className="text-muted-foreground text-sm">
                Regular briefings on regulatory changes, emerging risks, and best practices.
              </p>
            </div>

            <div className="bg-card rounded-xl p-6 shadow-lg border text-center">
              <FileText className="h-10 w-10 text-techne-purple mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">Resource Library</h3>
              <p className="text-muted-foreground text-sm">
                Access to templates, frameworks, and board-ready materials.
              </p>
            </div>

            <div className="bg-card rounded-xl p-6 shadow-lg border text-center">
              <Bell className="h-10 w-10 text-techne-purple mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">Priority Alerts</h3>
              <p className="text-muted-foreground text-sm">
                Timely notifications on critical AI governance developments.
              </p>
            </div>
          </div>

          <div className="bg-card rounded-2xl p-8 md:p-12 max-w-4xl mx-auto shadow-lg border">
            <div className="flex items-start gap-6 mb-8">
              <Users className="h-12 w-12 text-techne-purple flex-shrink-0" />
              <div>
                <h2 className="text-2xl font-bold mb-2">What's Included</h2>
                <p className="text-muted-foreground">
                  Everything your board needs to maintain effective AI oversight.
                </p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4 mb-8">
              {[
                "Monthly governance briefings",
                "Regulatory update summaries",
                "Board presentation templates",
                "Risk assessment tools",
                "Peer benchmarking insights",
                "Priority email support",
                "Quarterly strategy calls",
                "Early access to new resources"
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                  <span>{item}</span>
                </div>
              ))}
            </div>

            <div className="text-center pt-6 border-t">
              <p className="text-muted-foreground mb-4">
                Interested in our governance subscription?
              </p>
              <Button asChild size="lg">
                <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Book a Chair Call
                </a>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default SubscriptionPage;
